import { Item, ItemTree, objectMap } from '@/utils/explorer';
import { enableMapSet } from 'immer';
import { PropsWithChildren, useMemo } from 'react';
import { create } from 'zustand';

enableMapSet();

import { immer } from 'zustand/middleware/immer';
import { ExplorerService, restoreSize, restoreExpanded, backupExpanded, backupSize, ExplorerContext } from './useExplorerProvider';

interface ExplorerProviderProps {
  prefix?: string;
  directories: Record<
    string,
    {
      items: Record<string, Item>;
      tree: ItemTree;
    }
  >;
}

export function ExplorerProvider({ children, directories, prefix }: PropsWithChildren<ExplorerProviderProps>) {
  const store = useMemo(
    () =>
      create(
        immer<ExplorerService>((set, get) => {
          return {
            prefix: prefix || "",
            tags: [],
            view: {
              size: restoreSize()
            },
            directories: objectMap(directories, (directory, id) => ({
              ...directory,
              expanded: restoreExpanded(id)
            })),

            setOpenState: (directoryId, id, isOpen) => {
              set(state => {
                // If closing, remove id from selected
                if (!isOpen) {
                  state.directories[directoryId].expanded.delete(id);
                  return;
                }
                // If opening, add id and all parents to selected
                const item = state.directories[directoryId].items[id];
                item.path.forEach(pathId => {
                  state.directories[directoryId].expanded.add(pathId);
                });
              });
              const state = get();
              const selectedForDirectory = state.directories[directoryId].expanded;
              backupExpanded(directoryId, selectedForDirectory);
            },
            setViewSize: size => {
              set(state => {
                state.view.size = size;
              });
              backupSize(size);
            },
            setExpandedTags: tags => {
              set(state => {
                state.tags = tags;
              });
            },
            setExpandedSeason: season => {
              set(state => {
                state.season = season;
              });
            }
          };
        })
      ),
    [directories, prefix]
  );

  return <ExplorerContext.Provider value={store}>{children}</ExplorerContext.Provider>;
}
